// pages/vendors/intern/index.tsx
import {
  createInternVendorColumns,
  InternVendorActionType,
} from "@/colDef/internColDef";
import {
  Button,
  Card,
  CardContent,
  CustomAlertDialog,
  DataTable,
  ErrorMessage,
  PageContainer,
  Pagination,
  Spinner,
} from "@/components";
import InternVendorDialog from "@/components/formDialog/InternVendorDialog";
import Search from "@/components/Search";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useDeleteInternVendorMutation,
  useInternVendorsQuery,
} from "@/hooks/api/internVendor.queries";
import useDebounce from "@/hooks/useDebounce";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import { usePagination } from "@/hooks/usePagination";
import {
  InternVendorParamsType,
  InternVendorStatus,
  InternVendorType,
} from "@/services/internVendor.service";
import { Filter, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const InternVendorsPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<InternVendorStatus | "all">(
    "all"
  );

  const { page, limit, setPage, resetPage } = usePagination({
    initialLimit: 10,
  });

  const debouncedSearch = useDebounce(searchTerm, 400);

  const deleteDialog = useAlertDialog();
  const vendorDialog = useFormDialog({
    mode: "create" as "create" | "edit",
    vendorId: "",
    vendorData: null as InternVendorType | null,
  });

  const filters: InternVendorParamsType = useMemo(
    () => ({
      limit,
      page,
      sortBy: "createdAt",
      sortOrder: "desc",
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(statusFilter !== "all" && { status: statusFilter }),
    }),
    [debouncedSearch, statusFilter, page, limit]
  );

  const { data, isLoading, isError, refetch } = useInternVendorsQuery(filters);
  const deleteMutation = useDeleteInternVendorMutation(filters);

  const vendors = data?.vendors ?? [];
  const pagination = data?.metaData?.pagination;
  const totalEntries = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(deleteDialog.state.mantraId, {
      onSuccess: () => deleteDialog.close(),
    });
  }, [deleteMutation, deleteDialog]);

  const handleActionClick = useCallback(
    (payload: InternVendorActionType) => {
      if (payload.action === "delete") {
        deleteDialog.open({
          message: (
            <span>
              Are you sure you want to delete{" "}
              <strong className="text-gray-800">{payload.name}</strong>? This
              action cannot be undone.
            </span>
          ),
          mantraId: payload.id,
          mantraTitle: payload.name,
          action: "delete",
        });
      } else if (payload.action === "edit") {
        const vendorData = vendors.find((v) => v._id === payload.id) ?? null;
        vendorDialog.open({ mode: "edit", vendorId: payload.id, vendorData });
      }
    },
    [deleteDialog, vendorDialog, vendors]
  );

  const handleCreateClick = () => {
    vendorDialog.open({ mode: "create", vendorId: "", vendorData: null });
  };

  const columns = useMemo(
    () => createInternVendorColumns(handleActionClick),
    [handleActionClick]
  );

  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load internship vendors."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <PageContainer className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Internship Vendors</h1>
        <Button
          onClick={handleCreateClick}
          className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800"
        >
          <Plus size={16} />
          Add New
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-3">
        <Search
          placeholder="Search..."
          value={searchTerm}
          onValueChange={(val) => {
            setSearchTerm(val);
            resetPage();
          }}
          className={{ inputGroupClass: "max-w-xs" }}
        />

        <Select
          value={statusFilter}
          onValueChange={(val) => {
            setStatusFilter(val as typeof statusFilter);
            resetPage();
          }}
        >
          <SelectTrigger className="w-36 gap-2">
            <Filter size={14} className="text-gray-500" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <DataTable<InternVendorType, any>
              columns={columns}
              data={vendors}
              onRowClick={(vendor) => navigate(`/vendors/intern/${vendor._id}`)}
            />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        totalEntries={totalEntries}
        currentCount={vendors.length}
        onPageChange={setPage}
      />

      {/* Vendor Create / Edit Dialog */}
      <InternVendorDialog
        mode={vendorDialog.state.mode}
        isOpen={vendorDialog.state.isOpen}
        onOpenChange={(open) => !open && vendorDialog.close()}
        filters={filters}
        vendorId={vendorDialog.state.vendorId}
        vendorData={vendorDialog.state.vendorData}
        onCancel={vendorDialog.close}
      />

      {/* Delete Confirmation Dialog */}
      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deletion"
        description={deleteDialog.state.message}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
};

export default InternVendorsPage;
